import {IndexedDBCache, Tracker} from "finhelper"
import {Api} from "laravel-request";
import {call, put, select, takeLatest} from 'redux-saga/effects'

import { clearAtonSessionCaches } from "#app/domain/funnel/aton/clearAtonSessionCaches";
import { loadAuthSessionSnapshot } from "#app/shared/lib/auth/authSessionStorage";
import { fetchProfileWithRetry } from "#app/shared/lib/auth/fetchProfileWithRetry";
import CacheHelper from "#app/shared/lib/cache/CacheHelper";
import FileHelper from "#app/shared/lib/files/FileHelper";
import * as authActions from '#app/store/actions/auth'
import type {AuthFormActionPayload, GetUserActionPayload, LogoutActionPayload} from '#app/store/actions/auth'
import * as interfaceActions from '#app/store/actions/interface'
import {handleNotify} from "#app/store/sagas/jobs";

declare const history: { push: (path: string) => void };

interface AuthFormAction {
    payload: AuthFormActionPayload;
}

interface LogoutAction {
    payload: LogoutActionPayload;
}

interface GetUserAction {
    payload?: GetUserActionPayload;
}

function* login(action: AuthFormAction) {
    try {
        const {form, success, error, obj} = action.payload;

        const promise = new Promise((resolve, reject) => {
            let request = Api.post('auth', 'login', form).withoutNotify((status) => {
                return status !== 200;
            });

            if (obj) {
                request = request.withValidateForm(obj);
            }

            request.call(((response: any, status: any, xhr: any) => {
                success(response, status, xhr);
                resolve(response);
            }) as any, (errors: any) => {
                console.error(errors);
                error(errors);
                reject(errors);
            });
        });

        const response = yield promise;

        yield put(authActions.handleGetUser(response as any));
        yield put(authActions.handleAuth());

        if (action.payload && typeof action.payload.callback === "function") {
            yield call(action.payload.callback);
        }
    } catch (err) {
        console.error(err);
    }
}

function* register(action: AuthFormAction) {
    try {
        const {form, success, error, obj} = action.payload;

        const promise = new Promise((resolve, reject) => {
            let request = Api.post('auth', 'register', form).withoutNotify();

            if (obj) {
                request = request.withValidateForm(obj);
            }

            request.call(((response: any, status: any, xhr: any) => {
                Tracker.reachGoal('register_finished', {});
                success(response, status, xhr);
                resolve(response);
            }) as any, (errors: any) => {
                console.error(errors);
                error(errors);
                reject(errors);
            });
        });

        const response = yield promise;
        yield put(authActions.handleGetUser(response as any));
        yield put(authActions.handleAuth());

        if (action.payload && typeof action.payload.callback === "function") {
            yield call(action.payload.callback);
        }

    } catch (err) {
        console.error(err);
    }
}

function* requestRegister(action: AuthFormAction) {
    try {
        const {form, success, error, obj} = action.payload;

        const promise = new Promise((resolve, reject) => {
            let request = Api.post('auth', 'request-register', form).withoutNotify();

            if (obj) {
                request = request.withValidateForm(obj);
            }

            request.call(((response: any, status: any, xhr: any) => {
                Tracker.reachGoal('request_register_code', {});
                success(response, status, xhr);
                resolve(response);
            }) as any, (errors: any) => {
                console.error(errors);
                error(errors);
                reject(errors);
            });
        });

        yield promise;
    } catch (err) {
        console.error(err);
    }
}

function* forgot(action: AuthFormAction) {
    try {
        const {form, success, error, obj} = action.payload;

        const promise = new Promise((resolve, reject) => {
            let request = Api.post('auth', 'forgot', form).withoutNotify();

            if (obj) {
                request = request.withValidateForm(obj);
            }

            request.call(((response: any, status: any, xhr: any) => {
                success(response, status, xhr);
                resolve(response);
            }) as any, (errors: any) => {
                console.error(errors);
                error(errors);
                reject(errors);
            });
        });

        const _response = yield promise;

        if (action.payload && typeof action.payload.callback === "function") {
            yield call(action.payload.callback);
        }

    } catch (err) {
        console.error(err);
    }
}

function* requestForgot(action: AuthFormAction) {
    try {
        const {form, success, error, obj} = action.payload;

        const promise = new Promise((resolve, reject) => {
            let request = Api.post('auth', 'request-forgot', form).withoutNotify();

            if (obj) {
                request = request.withValidateForm(obj);
            }

            request.call(((response: any, status: any, xhr: any) => {
                success(response, status, xhr);
                resolve(response);
            }) as any, (errors: any) => {
                console.error(errors);
                error(errors);
                reject(errors);
            });
        });

        yield promise;
    } catch (err) {
        console.error(err);
    }
}

function* logout(action: LogoutAction) {
    try {
        const {success, error} = action.payload;

        lastProfileFetchedAt = 0;
        profileRequestInFlight = false;

        yield put(authActions.handleLogout());
        yield put(interfaceActions.handleLogoutInterface());
        clearAtonSessionCaches((action) => put(action as any));
        yield call(history.push, '/');

        const promise = new Promise((resolve, _reject) => {
            Api.get('auth', 'logout').call((response) => {
                if (response.result === 'success') {
                    success();
                    resolve(response);
                }
            }, (errors) => {
                console.error(errors);
                error(errors);
            });
        });

        const _response = yield promise;
        yield handleNotify({data: {jobs: []}});

    } catch (err) {
        console.error(err);
    }
}

function* _getServer() {
    try {
        yield call(() =>
            IndexedDBCache.get(CacheHelper.SERVER_DATA).then((data: any) => {
                if (!data) {
                    Api.makeRequest({
                        method: "GET",
                        url: process.env.REACT_APP_API_URL + "/api/v1/call/system/index",
                        success: ({ data: payload }) => {
                            FileHelper.data = payload;
                            IndexedDBCache.set(CacheHelper.SERVER_DATA, payload);
                        },
                        error: () => {},
                    });
                } else {
                    FileHelper.data = data;
                }
            }),
        );
    } catch (err) {
        console.warn(err);
    }
}

const PROFILE_SESSION_CACHE_MS = 60 * 1000;
let lastProfileFetchedAt = 0;
let profileRequestInFlight = false;

function* restoreSessionFromSnapshot(): Generator<any, boolean, any> {
    const snapshot = loadAuthSessionSnapshot();
    if (!snapshot?.data?.id) {
        return false;
    }

    yield put(authActions.handleGetUser({ result: 'success', data: snapshot.data }));
    yield put(authActions.handleAuth());
    return true;
}

function* getUser(action: GetUserAction) {
    const payload: GetUserActionPayload = action.payload ?? {};
    const forceRefresh = Boolean(payload.force);

    const authState: any = yield select((state: any) => state.auth);
    const apiToken = localStorage.getItem('api_token');

    if (!apiToken) {
        if (authState?.auth) {
            yield put(authActions.handleLogout());
        }
        if (typeof payload.fallback === 'function') {
            yield call(payload.fallback);
        }
        return;
    }

    let hasAuthenticatedUser =
        authState?.auth &&
        authState?.data &&
        authState.data.id != null &&
        authState.data.id !== '';

    if (!hasAuthenticatedUser) {
        hasAuthenticatedUser = yield* restoreSessionFromSnapshot();
    }

    const profileCacheIsFresh =
        Date.now() - lastProfileFetchedAt < PROFILE_SESSION_CACHE_MS;

    if (
        !forceRefresh &&
        hasAuthenticatedUser &&
        profileCacheIsFresh
    ) {
        if (typeof payload.callback === 'function') {
            yield call(payload.callback);
        }
        return;
    }

    if (profileRequestInFlight && !forceRefresh) {
        if (typeof payload.callback === 'function') {
            yield call(payload.callback);
        }
        return;
    }

    profileRequestInFlight = true;

    try {
        // Вход по токену: одноразовый token из URL/памяти больше не нужен
        localStorage.removeItem('token');

        const response: any = yield call(fetchProfileWithRetry);

        if (response?.data?.api_token) {
            localStorage.setItem('api_token', response.data.api_token);
        }

        if (response?.result === 'success') {
            lastProfileFetchedAt = Date.now();
            yield put(authActions.handleGetUser(response));

            if (payload.load !== false) {
                yield put(authActions.handleAuth());
            }

            if (typeof payload.callback === 'function') {
                yield call(payload.callback);
            }
            return;
        }

        if (hasAuthenticatedUser) {
            if (typeof payload.callback === 'function') {
                yield call(payload.callback);
            }
            return;
        }

        if (typeof payload.fallback === 'function') {
            yield call(payload.fallback);
        }
    } catch (err) {
        if (!hasAuthenticatedUser) {
            hasAuthenticatedUser = yield* restoreSessionFromSnapshot();
        }

        if (hasAuthenticatedUser || localStorage.getItem('api_token')) {
            if (typeof payload.callback === 'function') {
                yield call(payload.callback);
            }
            return;
        }

        if (typeof payload.fallback === 'function') {
            yield call(payload.fallback);
        }
    } finally {
        profileRequestInFlight = false;
    }
}

function* socketsConnecting(_action: any) {
}

function* postUser(action: AuthFormAction) {
    try {
        const {form, success, error, obj} = action.payload;

        const promise = new Promise((resolve, reject) => {
            let request = Api.post('profile', 'profile', form);

            if (obj) {
                request = request.withValidateForm(obj);
            }

            request.call(((response: any, status: any, xhr: any) => {
                success(response, status, xhr);
                resolve(response);
            }) as any, (errors: any) => {
                console.error(errors);
                error(errors);
                reject(errors);
            });
        });

        const response = yield promise;

        yield put(authActions.handleGetUser(response as any));
        yield put(authActions.handleAuth());
    } catch (err) {
        console.error(err);
    }
}

function* handleAuth(_action: any) {
    try {
        const state: any = yield select();
        if (state.auth && state.auth.data) {
            yield put(authActions.socketsConnecting({
                userId: state.auth.data.id,
                role: 'crm'
            }));
        }

        yield put(interfaceActions.getContacts())
        yield put(interfaceActions.getClients())
        yield put(interfaceActions.getCourses());
        yield put(interfaceActions.getCurrencies());

    } catch (err) {
        console.error(err);
    }
}

export default function* watcher() {
    yield takeLatest(authActions.login, login)
    yield takeLatest(authActions.register, register)
    yield takeLatest(authActions.requestRegister, requestRegister)
    yield takeLatest(authActions.forgot, forgot)
    yield takeLatest(authActions.requestForgot, requestForgot)
    yield takeLatest(authActions.logout, logout)
    yield takeLatest(authActions.getUser, getUser)
    yield takeLatest(authActions.postUser, postUser)
    yield takeLatest(authActions.handleAuth, handleAuth)
    yield takeLatest(authActions.socketsConnecting, socketsConnecting)
}
